<!--
@component
MatchEntry

Single bracket cell: an optional anchor wrapping a `MatchCard`, plus the
venue/date metadata below. Computes the shared `MatchSummary` once and
forwards it to `MatchCard`, so the card markup doesn't have to call
`get*Team` / `get*Score` / `getWinner` again.
-->

<script lang="ts">
  import { appState } from '$lib/stores/index.svelte';
  import { isMatchSoon } from '$utils/dates';
  import type { Match, Venue } from '$utils/getMatchStats';
  import getMatchPath from '$utils/getMatchPath';
  import { getMatchSummary } from '../content';
  import MatchCard from './MatchCard.svelte';

  let {
    match,
    round,
    widthRect,
    heightRect,
    venues,
  }: {
    match: Match & {
      path?: string;
      label?: string;
      localDateStamp?: { month: string; day: string; time: string };
    };
    round: string;
    widthRect: number;
    heightRect: number;
    venues: Venue[];
  } = $props();

  let summary = $derived(getMatchSummary(match, venues));
  let matchDate = $derived(match?.localDateStamp);
  let matchSoon = $derived(matchDate ? isMatchSoon(match) : false);
  // Defer to `getMatchPath` so the embed branch (`appState.embed`)
  // flows through automatically. `'.'` means one contestant is still
  // unknown, in which case the card stays unlinked.
  let matchPath = $derived(getMatchPath(match));
  let hasLink = $derived(Boolean(matchPath && matchPath.url !== '.'));
  let matchHref = $derived(hasLink ? matchPath?.url : undefined);
</script>

{#snippet card()}
  <MatchCard {widthRect} {heightRect} {match} {round} {summary} />
{/snippet}

{#if hasLink}
  <a
    href={matchHref}
    target={appState.embed ? '_top' : undefined}
    title={match.label}
    class:loading={appState.isLoading}
  >
    {@render card()}
  </a>
{:else}
  {@render card()}
{/if}

<div class="match-date-location mt-0.5 font-sans">
  {#if summary.venue}
    <span class="location">{summary.venue.city || ''}</span>
  {/if}
  <span class="location-date-separator">&nbsp;&middot;</span>
  {#if match?.matchInfo?.date && matchDate}
    <span class="date font-medium">
      {#if matchSoon}
        {matchDate.time}
      {:else}
        {matchDate.month}
        {matchDate.day}
      {/if}
    </span>
  {/if}
</div>

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  a {
    text-decoration: inherit;
    color: inherit;
    display: block;
    border-radius: calc(0.5 * var(--theme-style-border-radius));

    &.loading {
      pointer-events: none;
      cursor: default;
    }

    @include mixins.hover-supported() {
      opacity: 0.9;
      text-decoration: inherit;
      color: inherit;
    }
  }

  .match-date-location {
    margin-inline-start: auto;
    max-width: 95%;
    position: relative;
    text-align: right;
    @include mixins.text-xs;
    @include mixins.font-sans;
    @include mixins.text-secondary;
    @include mixins.leading-tighter;
  }

  .date {
    white-space: nowrap;
  }
</style>
